import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button, Alert, Spinner } from 'react-bootstrap';
import { useSearchParams, useLocation } from 'react-router-dom';

import { useAuth } from '@/shared/contexts/AuthContext';
import ChatSidebar from '@/features/chat/components/ChatSidebar';
import ChatInput from '@/features/chat/components/ChatInput';
import MapPayload from '@/features/chat/components/MapPayload';
import ChatMapPanel from '@/features/chat/components/ChatMapPanel';
import useUserCommunities from '@/hooks/useUserCommunities';
import ShareToCommunityModal from '@/components/common/ShareToCommunityModal';
import logger from '@/shared/utils/logger';
import { getTextareaMinHeight } from '@/shared/utils/chatHelpers';
import './ChatPill.css';
import './BiliChat.css';
import {
    loadConversationsFromEngine,
    getConversationFromEngine,
    deleteConversationFromEngine,
    getEngineStats,
    connectToEngine,
    waitForEngineReady,
    disconnectFromEngine,
    sendMessageToEngine,
    createNewEngineConversation,
    switchEngineConversation,
    getCurrentThreadId,
    getEngineErrorType,
    onEngineClose,
    offEngineClose,
    WS_ERROR_TYPES,
} from '@/shared/services/engineChatService';
import babyBili from '@/assets/img/icons/svg/babyBili.svg';
import profileIcon from '@/assets/img/icons/svg/profileIcon.svg';
import SEO from '@/components/common/SEO';

/**
 * Strips [page_context: {...}] prefix from messages sent by the chat widget.
 * The engine stores context metadata inline; we hide it from the user display.
 */
function stripPageContext(text) {
    if (!text || !text.startsWith('[page_context:')) return text;
    // Find the matching closing bracket by tracking brace/bracket depth
    let depth = 0;
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (ch === '[' || ch === '{') depth++;
        else if (ch === ']' || ch === '}') depth--;
        if (depth === 0) return text.slice(i + 1).trim();
    }
    return text;
}

export default function BiliChat() {
    const { user } = useAuth();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialThreadRef = useRef(searchParams.get('thread'));
    const promotedMessagesRef = useRef(
        location.state?.promotedMessages ?? null
    );
    // When navigated here from Advanced Query, this holds the assembled query
    // text to auto-send into a new expert-persona conversation (consumed once).
    const expertQueryRef = useRef(location.state?.expertQuery ?? null);
    // Holds the latest sendQuery closure so the auto-send effect can invoke it
    // without listing it as a dependency.
    const sendQueryRef = useRef(null);
    const [selectedChatId, setSelectedChatId] = useState(null);
    const [selectedChat, setSelectedChat] = useState(null);
    const [chatInput, setChatInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [chats, setChats] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMessage, setIsLoadingMessage] = useState(false);
    const [error, setError] = useState(null);
    const [errorType, setErrorType] = useState(null); // 'permission_denied', 'connection_error', 'load_error'
    const [usageStats, setUsageStats] = useState(null);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
        () => localStorage.getItem('bili-sidebar-collapsed') === 'true'
    );

    // Share-to-community: single modal instance for all bot messages
    const { hasCommunities, loading: communitiesLoading } =
        useUserCommunities();
    const [shareContent, setShareContent] = useState(null);
    const showShareButtons = !!user && !communitiesLoading && hasCommunities;

    // Connect to engine and load chats on component mount
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                setIsLoading(true);
                setError(null);
                setErrorType(null);

                // Step 1: Connect to WebSocket engine (critical - blocks page if fails).
                // Arriving from Advanced Query connects on the expert persona
                // route so the new conversation runs the Expert Query persona.
                try {
                    await connectToEngine(
                        null,
                        expertQueryRef.current ? 'expert' : null
                    );
                } catch (wsError) {
                    // Check for specific WebSocket error types
                    const wsErrorType = getEngineErrorType();
                    if (wsErrorType === WS_ERROR_TYPES.PERMISSION_DENIED) {
                        setErrorType('permission_denied');
                        setError(
                            'You do not have permission to access this feature. Please contact a system administrator to request access.'
                        );
                    } else if (wsErrorType === WS_ERROR_TYPES.AUTH_ERROR) {
                        setErrorType('auth_error');
                        setError('Authentication failed. Please log in again.');
                    } else {
                        setErrorType('connection_error');
                        setError(
                            'Unable to connect to the chat service. Please try again later.'
                        );
                    }
                    logger.error('WebSocket connection failed:', wsError);
                    return; // Stop here - can't proceed without WebSocket
                }

                // Step 2: Load chats and stats from REST API (non-critical - show warning if fails)
                try {
                    const [chatsData, statsData] = await Promise.all([
                        loadConversationsFromEngine(true), // throwOnError = true
                        getEngineStats(),
                    ]);
                    setChats(chatsData);
                    setUsageStats(statsData);

                    // Auto-select conversation from ?thread= URL param
                    if (initialThreadRef.current) {
                        setSelectedChatId(initialThreadRef.current);
                        initialThreadRef.current = null;
                    }
                } catch (apiError) {
                    logger.error('Failed to load chat history:', apiError);
                    // Show dismissible warning - user can still start new chats
                    setError(
                        'Unable to load chat history. You can still start new conversations.'
                    );
                    // Don't set errorType - allow the page to render normally with the warning
                }
            } finally {
                setIsLoading(false);
            }
        };

        // Handle WebSocket close events (e.g., permission denied after initial connect)
        const handleClose = (closeData) => {
            if (closeData.errorType === WS_ERROR_TYPES.PERMISSION_DENIED) {
                setErrorType('permission_denied');
                setError(
                    'You do not have permission to access this feature. Please contact a system administrator to request access.'
                );
            }
        };

        loadInitialData();
        onEngineClose(handleClose);

        // Cleanup: disconnect from WebSocket on unmount
        return () => {
            offEngineClose(handleClose);
            disconnectFromEngine();
        };
    }, []);

    // Auto-send an assembled Advanced Query into the fresh expert-persona
    // conversation once the page has connected. Runs once — the ref is consumed
    // so a re-render or refresh does not resend.
    //
    // isLoading going false only means the load function returned; it does not
    // guarantee the WebSocket handshake has completed. Sending in that window
    // hits createNewConversation()'s "WebSocket not connected" guard and fails
    // (a race that only shows up on slower sockets, e.g. WSL2). So we wait for
    // the socket to actually be open before sending, and only consume the ref
    // once we're ready to send.
    useEffect(() => {
        if (isLoading || errorType) return;
        if (!expertQueryRef.current) return;

        let cancelled = false;
        (async () => {
            try {
                await waitForEngineReady();
            } catch (err) {
                logger.error(
                    'Advanced Query auto-send aborted; socket not ready:',
                    err
                );
                return;
            }
            if (cancelled || !expertQueryRef.current) return;
            const query = expertQueryRef.current;
            expertQueryRef.current = null;
            sendQueryRef.current?.(query);
        })();

        return () => {
            cancelled = true;
        };
    }, [isLoading, errorType]);

    // Persist sidebar collapsed state across refreshes
    useEffect(() => {
        localStorage.setItem('bili-sidebar-collapsed', isSidebarCollapsed);
    }, [isSidebarCollapsed]);

    // Keep ?thread= URL param in sync so refresh restores the current chat
    useEffect(() => {
        if (selectedChatId) {
            setSearchParams({ thread: selectedChatId }, { replace: true });
        } else {
            setSearchParams({}, { replace: true });
        }
    }, [selectedChatId, setSearchParams]);

    // expand textarea height based on content
    const textareaRef = useRef(null);

    // Ref for scrolling to bottom of messages
    const messagesEndRef = useRef(null);

    // Flag to skip loading chat details when creating a new conversation
    // This prevents the useEffect from overwriting the selectedChat with user message
    const skipChatDetailsLoadRef = useRef(false);

    // Scroll to bottom of messages when chat changes or new messages arrive
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if (selectedChat?.messages?.length > 0) {
            scrollToBottom();
        }
    }, [selectedChat?.messages?.length]);

    // Load selected chat details when chat is selected and switch WebSocket connection
    useEffect(() => {
        const loadChatDetails = async () => {
            if (!selectedChatId) {
                setSelectedChat(null);
                return;
            }

            // Skip loading if we just created a new conversation in handleSendMessage
            // This prevents overwriting the selectedChat that already has the user message
            if (skipChatDetailsLoadRef.current) {
                skipChatDetailsLoadRef.current = false;
                return;
            }

            // Seed with widget messages immediately to avoid blank flash
            // while the REST API races to persist the just-completed conversation.
            const promoted = promotedMessagesRef.current;
            if (promoted) {
                const seedMessages = promoted.filter(
                    (m) => m.id !== 'widget-greeting'
                );
                setSelectedChat({
                    id: selectedChatId,
                    title: 'Conversation',
                    messages: seedMessages,
                    tags: [],
                    metadata: {
                        category: 'general',
                        messageCount: seedMessages.length,
                    },
                });
                promotedMessagesRef.current = null;
            }

            try {
                // Only switch WebSocket if we're not already connected to this thread
                const currentThreadId = getCurrentThreadId();
                if (currentThreadId !== selectedChatId) {
                    await switchEngineConversation(selectedChatId);
                }

                // Load chat history from REST API (reconciles with server-persisted data)
                const chatData =
                    await getConversationFromEngine(selectedChatId);
                setSelectedChat(chatData);
            } catch (err) {
                logger.error('Failed to load chat details:', err);
                // Only show error if we have no promoted messages to fall back on
                if (!promoted) {
                    setError('Failed to load chat messages.');
                }
            }
        };

        loadChatDetails();
    }, [selectedChatId]);

    const handleNewChat = () => {
        setSelectedChatId(null);
        setChatInput('');
        setError(null);
        setIsMobileSidebarOpen(false);
    };

    const handleChatSelect = (chatId) => {
        setSelectedChatId(chatId);
        setError(null);
        setIsMobileSidebarOpen(false);
    };

    // Core send: create-or-reuse a conversation and exchange a message. Shared
    // by the chat input (handleSendMessage) and the Advanced Query auto-send.
    const sendQuery = async (messageText) => {
        if (!messageText || isLoadingMessage) return;

        setError(null);

        try {
            // Add user message to UI immediately for better UX
            const userMessage = {
                id: `temp-user-${Date.now()}`,
                text: messageText,
                sender: 'user',
                timestamp: new Date(),
            };

            // Store the current chat ID (either existing or newly created)
            let currentChatId = selectedChatId;

            // Make sure the socket is actually open before creating a
            // conversation or sending. On a cold start (or a reconnect window)
            // the socket may still be handshaking when the first message fires;
            // createNewConversation()/sendMessage() throw "WebSocket not
            // connected" in that window and there is no retry around the
            // create step. Waiting here — rather than failing immediately — is
            // what makes the first message of a session reliable.
            await waitForEngineReady();

            if (!selectedChatId) {
                // Create new conversation via WebSocket first
                const { threadId } = await createNewEngineConversation();
                // Skip the useEffect that loads chat details to prevent overwriting
                // the selectedChat with user message we're about to set
                skipChatDetailsLoadRef.current = true;
                setSelectedChatId(threadId);
                currentChatId = threadId;

                // Create temporary title from user's message (truncate at 40 chars)
                const tempTitle =
                    messageText.length > 40
                        ? `${messageText.substring(0, 40)}...`
                        : messageText;

                // Set selected chat with user message
                setSelectedChat({
                    id: threadId,
                    title: tempTitle,
                    messages: [userMessage],
                    lastActivity: new Date(),
                    metadata: { category: 'general', messageCount: 1 },
                });
            } else {
                // Update existing chat with user message
                setSelectedChat((prevChat) => ({
                    ...prevChat,
                    messages: [...prevChat.messages, userMessage],
                }));
            }

            // Set loading indicator
            setIsLoadingMessage(true);

            // Send message via WebSocket — retry once with reconnect if WS dropped
            let botResponse;
            try {
                botResponse = await sendMessageToEngine(messageText);
            } catch {
                await switchEngineConversation(currentChatId);
                botResponse = await sendMessageToEngine(messageText);
            }

            // Add bot response and update title/tags
            const botMessage = {
                id: botResponse.id,
                text: botResponse.text,
                sender: 'bot',
                timestamp: botResponse.timestamp,
                // Map payload from the build_map_payload node. This is an
                // explicit field list, not a spread — any new field returned by
                // sendMessageToEngine has to be copied here or it is silently
                // dropped before render. Mirrored in hooks/useWidgetChat.js.
                map: botResponse.map,
            };

            setSelectedChat((prevChat) => ({
                ...prevChat,
                title: botResponse.title || prevChat.title,
                tags: botResponse.tags || prevChat.tags,
                messages: [...prevChat.messages, botMessage],
            }));

            // Update conversation list — separate call, batched by React in same microtask
            if (currentChatId) {
                setChats((prevChats) => {
                    const chatIndex = prevChats.findIndex(
                        (c) => c.id === currentChatId
                    );
                    if (chatIndex >= 0) {
                        const updatedChats = [...prevChats];
                        updatedChats[chatIndex] = {
                            ...updatedChats[chatIndex],
                            title:
                                botResponse.title ||
                                updatedChats[chatIndex].title,
                            tags:
                                botResponse.tags ||
                                updatedChats[chatIndex].tags,
                            messageCount:
                                (updatedChats[chatIndex].messageCount ?? 0) + 2,
                            lastMessage: botResponse.text.substring(0, 100),
                            lastActivity: new Date(),
                        };
                        return updatedChats;
                    } else {
                        return [
                            {
                                id: currentChatId,
                                title: botResponse.title || '',
                                tags: botResponse.tags || [],
                                messageCount: 2,
                                lastMessage: botResponse.text.substring(0, 100),
                                lastActivity: new Date(),
                                category: 'general',
                            },
                            ...prevChats,
                        ];
                    }
                });
            }

            // Update stats only (don't reload entire conversation list to avoid race condition)
            try {
                const statsData = await getEngineStats();
                setUsageStats(statsData);
            } catch (err) {
                logger.error('Failed to update stats:', err);
            }
        } catch (error) {
            logger.error('Failed to send message:', error);
            setError('Failed to send message. Please try again.');
        } finally {
            setIsLoadingMessage(false);
        }
    };

    // Keep a live reference so the auto-send effect always invokes the latest
    // sendQuery closure without needing it in its dependency array.
    sendQueryRef.current = sendQuery;

    const handleSendMessage = async () => {
        if (!chatInput.trim() || isLoadingMessage) return;
        const messageText = chatInput.trim();
        setChatInput(''); // Clear input immediately — ChatInput resets height via useEffect
        await sendQuery(messageText);
    };

    const handleDeleteChat = async (chatId, e) => {
        e.stopPropagation(); // Prevent chat selection

        try {
            await deleteConversationFromEngine(chatId);

            // Remove from local state
            setChats((prevChats) => prevChats.filter((c) => c.id !== chatId));

            if (selectedChatId === chatId) {
                setSelectedChatId(null);
                setSelectedChat(null);
            }

            // Update usage stats
            const updatedStats = await getEngineStats();
            setUsageStats(updatedStats);
        } catch (error) {
            logger.error('Failed to delete chat:', error);
            setError('Failed to delete chat. Please try again.');
        }
    };
    const filteredChats = chats.filter((chat) => {
        const term = searchTerm.toLowerCase();
        const titleMatch = chat.title.toLowerCase().includes(term);
        const tagMatch = chat.tags?.some((tag) =>
            tag.toLowerCase().includes(term)
        );
        return titleMatch || tagMatch;
    });

    // Retry connection function for recoverable errors
    const handleRetry = async () => {
        setIsLoading(true);
        setError(null);
        setErrorType(null);

        try {
            // Step 1: Connect to WebSocket
            try {
                await connectToEngine();
            } catch (wsError) {
                const wsErrorType = getEngineErrorType();
                if (wsErrorType === WS_ERROR_TYPES.PERMISSION_DENIED) {
                    setErrorType('permission_denied');
                    setError(
                        'You do not have permission to access this feature. Please contact a system administrator to request access.'
                    );
                } else {
                    setErrorType('connection_error');
                    setError(
                        'Unable to connect to the chat service. Please try again later.'
                    );
                }
                logger.error('Retry WebSocket failed:', wsError);
                return;
            }

            // Step 2: Load data from REST API
            try {
                const [chatsData, statsData] = await Promise.all([
                    loadConversationsFromEngine(true),
                    getEngineStats(),
                ]);
                setChats(chatsData);
                setUsageStats(statsData);
            } catch (apiError) {
                logger.error('Failed to load chat history:', apiError);
                setError(
                    'Unable to load chat history. You can still start new conversations.'
                );
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Show permission denied screen (full page, no retry)
    if (errorType === 'permission_denied') {
        return (
            <section
                id="bili-chat"
                className="page-container mb-0 pb-0"
                aria-label="Bili chat"
            >
                <Container
                    fluid
                    className="h-100 d-flex align-items-center justify-content-center"
                >
                    <div className="text-center p-5">
                        <div className="mb-4">
                            <span
                                className="chat-status-icon"
                                role="img"
                                aria-label="lock"
                            >
                                🔒
                            </span>
                        </div>
                        <h1 className="h2 text-danger mb-3">Access Denied</h1>
                        <p className="text-muted mb-4 chat-status-message">
                            You do not have permission to access the Bili chat
                            feature. This feature requires researcher or
                            administrator privileges.
                        </p>
                        <p className="text-muted mb-4">
                            Please contact a system administrator to request
                            access.
                        </p>
                        <Button
                            variant="primary"
                            href="/dashboard"
                            className="mt-2"
                        >
                            Return to Dashboard
                        </Button>
                    </div>
                </Container>
            </section>
        );
    }

    // Show connection error screen with retry option
    if (errorType === 'connection_error' || errorType === 'auth_error') {
        return (
            <section
                id="bili-chat"
                className="page-container mb-0 pb-0"
                aria-label="Bili chat"
            >
                <Container
                    fluid
                    className="h-100 d-flex align-items-center justify-content-center"
                >
                    <div className="text-center p-5">
                        <div className="mb-4">
                            <span
                                className="chat-status-icon"
                                role="img"
                                aria-label="warning"
                            >
                                ⚠️
                            </span>
                        </div>
                        <h1 className="h2 text-warning mb-3">
                            {errorType === 'auth_error'
                                ? 'Authentication Error'
                                : 'Connection Error'}
                        </h1>
                        <p className="text-muted mb-4 chat-status-message">
                            {error ||
                                'Unable to connect to the chat service. Please try again later.'}
                        </p>
                        <div className="d-flex gap-3 justify-content-center">
                            <Button
                                variant="primary"
                                onClick={handleRetry}
                                className="mt-2"
                            >
                                Try Again
                            </Button>
                            <Button
                                variant="secondary"
                                href="/dashboard"
                                className="mt-2"
                            >
                                Return to Dashboard
                            </Button>
                        </div>
                    </div>
                </Container>
            </section>
        );
    }

    // Show loading screen during initial load
    if (isLoading) {
        return (
            <section
                id="bili-chat"
                className="page-container mb-0 pb-0"
                aria-label="Bili chat"
            >
                <Container
                    fluid
                    className="h-100 d-flex align-items-center justify-content-center"
                >
                    <div className="text-center" role="status">
                        <Spinner
                            animation="border"
                            variant="primary"
                            className="mb-3"
                        />
                        <span className="visually-hidden">Loading...</span>
                        <div>Loading your chats...</div>
                    </div>
                </Container>
            </section>
        );
    }

    return (
        <>
            <section
                id="bili-chat"
                className="page-container mb-0 pb-0"
                aria-label="Bili chat"
            >
                <SEO title="Bili Chat" />
                <Container
                    fluid
                    className="flex-grow-1 d-flex flex-column px-0 mb-0 pb-0"
                >
                    {error && (
                        <Alert
                            variant="warning"
                            className="m-3 mb-0 flex-shrink-0"
                            dismissible
                            onClose={() => setError(null)}
                        >
                            {error}
                        </Alert>
                    )}
                    <Row className="flex-grow-1 g-0 overflow-hidden">
                        <ChatSidebar
                            isMobileSidebarOpen={isMobileSidebarOpen}
                            isSidebarCollapsed={isSidebarCollapsed}
                            selectedChatId={selectedChatId}
                            searchTerm={searchTerm}
                            filteredChats={filteredChats}
                            usageStats={usageStats}
                            user={user}
                            onChatSelect={handleChatSelect}
                            onDeleteChat={handleDeleteChat}
                            onSearchChange={setSearchTerm}
                            onMobileClose={() => setIsMobileSidebarOpen(false)}
                            onNewChat={handleNewChat}
                            onToggleCollapse={() =>
                                setIsSidebarCollapsed((v) => !v)
                            }
                            onSuggestedQuestion={(q) => {
                                setChatInput(q);
                                textareaRef.current?.focus();
                            }}
                        />

                        {/* Main Chat Area */}
                        <Col
                            xs={12}
                            lg={8}
                            xl={9}
                            className="main-chat-col bg-white"
                        >
                            {/* Mobile top bar — floating pill with hamburger + new chat */}
                            <div className="d-flex align-items-center gap-1 px-2 py-2 bg-primary rounded-3 d-lg-none chat-mobile-topbar">
                                <button
                                    type="button"
                                    className="sidebar-icon-btn"
                                    onClick={() => setIsMobileSidebarOpen(true)}
                                    aria-label="Open sidebar"
                                >
                                    <i
                                        className="bi bi-list fs-5"
                                        aria-hidden="true"
                                    />
                                </button>
                                <button
                                    type="button"
                                    className="sidebar-icon-btn"
                                    onClick={handleNewChat}
                                    aria-label="New chat"
                                >
                                    <i
                                        className="bi bi-pencil-square fs-5"
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>
                            {/* Body: centers everything vertically when no active chat */}
                            <div
                                className={`chat-body-wrapper d-flex flex-column flex-grow-1 min-height-0${!selectedChat ? ' justify-content-center' : ''}`}
                            >
                                <div
                                    className={
                                        selectedChat
                                            ? 'd-flex flex-column flex-grow-1 min-height-0'
                                            : 'd-flex flex-column align-items-center text-center px-4'
                                    }
                                >
                                    {!selectedChat ? (
                                        // Welcome Screen
                                        <div className="d-flex flex-column align-items-center text-center px-1">
                                            <div className="mb-4">
                                                <div className="icon-circle bg-warning border border-primary w-75 fs-5 rounded-3 d-flex align-items-center justify-content-center">
                                                    <img
                                                        src={babyBili}
                                                        alt="Bili-Bot Icon"
                                                        className="bili-icon"
                                                    />
                                                </div>
                                            </div>
                                            <h1 className="text-primary fw-semibold bili-welcome-title mb-3">
                                                Hi, I&apos;m Bili!
                                            </h1>
                                            <p className="fs-5 mb-4">
                                                I&apos;m here to help you
                                                explore Colorado&apos;s
                                                sustainability landscape.
                                            </p>
                                            <div className="mb-4 bili-welcome-menu">
                                                <p className="mb-3 fw-medium text-center">
                                                    You can ask me about:
                                                </p>
                                                <div className="bili-badges-container">
                                                    <span className="badge bg-primary fw-normal bili-topic-badge">
                                                        Climate &amp; Resilience
                                                    </span>
                                                    <span className="badge bg-primary fw-normal bili-topic-badge">
                                                        Renewable Energy
                                                    </span>
                                                    <span className="badge bg-primary fw-normal bili-topic-badge">
                                                        Water Conservation
                                                    </span>
                                                    <span className="badge bg-primary fw-normal bili-topic-badge">
                                                        Affordable Housing
                                                    </span>
                                                    <span className="badge bg-primary fw-normal bili-topic-badge">
                                                        Public Health
                                                    </span>
                                                    <span className="badge bg-primary fw-normal bili-topic-badge">
                                                        Sustainable Living
                                                    </span>
                                                </div>
                                                {usageStats && (
                                                    <small className="text-muted d-block text-center mt-2">
                                                        {usageStats.dailyLimit >
                                                        0
                                                            ? `You have ${usageStats.dailyLimit - usageStats.dailyMessages} messages remaining today`
                                                            : 'Unlimited messages available'}
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        // Chat Messages
                                        <div
                                            className="flex-grow-1 overflow-auto chat-messages-area"
                                            role="log"
                                            aria-live="polite"
                                            aria-label="Chat messages"
                                        >
                                            <div className="chat-messages-inner d-flex flex-column gap-3">
                                                {selectedChat.messages.map(
                                                    (message) => (
                                                        <div
                                                            key={message.id}
                                                            className={`d-flex gap-3 ${message.sender === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
                                                        >
                                                            {message.sender ===
                                                                'bot' && (
                                                                <img
                                                                    src={
                                                                        babyBili
                                                                    }
                                                                    alt="Bili-Bot Icon"
                                                                    className="chat-message-icon flex-shrink-0"
                                                                />
                                                            )}
                                                            <div className="d-flex flex-column w-75">
                                                                <div
                                                                    className={`position-relative ${message.sender === 'bot' ? 'bot-message-wrapper' : ''}`}
                                                                >
                                                                    <div
                                                                        className={`message-bubble ${message.sender === 'user' ? 'user' : 'bot'}`}
                                                                    >
                                                                        {message.sender ===
                                                                        'bot' ? (
                                                                            <ReactMarkdown
                                                                                remarkPlugins={[
                                                                                    remarkGfm,
                                                                                ]}
                                                                                components={{
                                                                                    // Wrap wide tables so they scroll
                                                                                    // horizontally within the message
                                                                                    // bubble instead of overflowing the
                                                                                    // screen (common with query result
                                                                                    // tables from the expert persona).
                                                                                    table: (
                                                                                        props
                                                                                    ) => (
                                                                                        <div className="table-responsive">
                                                                                            <table
                                                                                                className="table table-sm table-bordered mb-2"
                                                                                                {...props}
                                                                                            />
                                                                                        </div>
                                                                                    ),
                                                                                }}
                                                                            >
                                                                                {
                                                                                    message.text
                                                                                }
                                                                            </ReactMarkdown>
                                                                        ) : (
                                                                            stripPageContext(
                                                                                message.text
                                                                            )
                                                                        )}
                                                                        {/* Mirrored in components/ChatMessages.jsx,
                                                                            which serves the floating widget. */}
                                                                        {message.sender ===
                                                                            'bot' && (
                                                                            <MapPayload
                                                                                payload={
                                                                                    message.map
                                                                                }
                                                                            />
                                                                        )}
                                                                        {message.mapVisualization && (
                                                                            <ChatMapPanel
                                                                                title={
                                                                                    message
                                                                                        .mapVisualization
                                                                                        .title ||
                                                                                    'Map view'
                                                                                }
                                                                                className="mt-2"
                                                                            />
                                                                        )}
                                                                    </div>
                                                                    {message.sender ===
                                                                        'bot' &&
                                                                        showShareButtons && (
                                                                            <div className="bot-message-actions position-absolute top-0 end-0 mt-2 me-2">
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-sm btn-link text-muted p-1"
                                                                                    onClick={() =>
                                                                                        setShareContent(
                                                                                            {
                                                                                                postType:
                                                                                                    'discussion',
                                                                                                content:
                                                                                                    message.text,
                                                                                            }
                                                                                        )
                                                                                    }
                                                                                    aria-label="Share to community"
                                                                                    title="Share to community"
                                                                                >
                                                                                    <i
                                                                                        className="bi bi-share"
                                                                                        aria-hidden="true"
                                                                                    />
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                </div>
                                                                <small
                                                                    className={`text-muted mt-1 ${message.sender === 'user' ? 'text-end' : 'text-start'} chat-small-text`}
                                                                >
                                                                    {new Date(
                                                                        message.timestamp
                                                                    ).toLocaleTimeString(
                                                                        [],
                                                                        {
                                                                            hour: '2-digit',
                                                                            minute: '2-digit',
                                                                        }
                                                                    )}
                                                                </small>
                                                            </div>
                                                            {message.sender ===
                                                                'user' && (
                                                                <img
                                                                    src={
                                                                        profileIcon
                                                                    }
                                                                    alt="Profile Icon"
                                                                    className="chat-message-icon flex-shrink-0"
                                                                />
                                                            )}
                                                        </div>
                                                    )
                                                )}

                                                {/* Loading indicator for bot response */}
                                                {isLoadingMessage && (
                                                    <div className="d-flex gap-3 justify-content-start">
                                                        <img
                                                            src={babyBili}
                                                            alt="Bili-Bot Icon"
                                                            className="chat-message-icon flex-shrink-0"
                                                        />
                                                        <div className="d-flex flex-column w-75">
                                                            <div className="p-3 rounded-4 bg-light text-dark">
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <Spinner
                                                                        animation="border"
                                                                        size="sm"
                                                                    />
                                                                    <span>
                                                                        Bili is
                                                                        thinking...
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                {/* Scroll anchor */}
                                                <div ref={messagesEndRef} />
                                            </div>
                                            {/* end chat-messages-inner */}
                                        </div>
                                    )}
                                </div>
                                {/* end messages/welcome area */}

                                {/* Chat Input */}
                                <div className="chat-input-area p-3 p-md-4 d-flex align-items-center justify-content-center">
                                    <ChatInput
                                        ref={textareaRef}
                                        value={chatInput}
                                        onValueChange={setChatInput}
                                        onSend={handleSendMessage}
                                        disabled={isLoadingMessage}
                                        placeholder="Chat With Bili"
                                        showExtraActions
                                        minHeight={getTextareaMinHeight}
                                        maxHeight={() =>
                                            window.innerWidth <= 575 ? 144 : 150
                                        }
                                    />
                                </div>
                                {/* end chat-input-area */}

                                {/* Disclaimer */}
                                <p className="text-center text-muted mb-2 chat-disclaimer">
                                    Bili can make mistakes. Check important
                                    info.
                                </p>
                            </div>
                            {/* end body wrapper */}
                        </Col>
                    </Row>
                </Container>
            </section>

            <ShareToCommunityModal
                show={!!shareContent}
                onHide={() => setShareContent(null)}
                shareContent={shareContent || {}}
            />
        </>
    );
}
